import React from "react";
import RichText from "@qlik/qlik/src/components/RichText";
import Media from "@qlik/qlik/src/components/Media";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import {
    getCardDescriptionText,
    getCardMedia,
    filterValidCta,
    renderSanitizedHtmlContent,
} from "@qlik/qlik/src/lib/util/helpers";
import Cta from "@qlik/qlik/src/components/Cta";
import * as styles from "./styles.module.css";

/**
 * ContentWithImage component.
 * Renders a section with a title, description, and media.
 * @param {Object} props - The component props.
 * @param {Object} props.data - The data for the section.
 * @param {Object} props.data.cardItem - The card item data.
 * @param {string} props.data.cardItem.title - The title of the section.
 * @param {string} props.data.cardItem.description - The description of the section.
 * @param {Object} props.data.cardItem.media - The media for the section.
 */
const ContentWithImage = ({ data }) => {
    const { cardItem, additionalConfiguration, customId, backgroundColor, id } = data ?? {};
    const { title, body, cta } = cardItem ?? {};

    const cardMedia = getCardMedia(data.cardItem);
    const cardEyebrowText = getCardDescriptionText(data.cardItem);
    const sectionPaddingClassName = getSpacingClassName(additionalConfiguration, "lg:py-30 py-5.5");
    const { sectionColorMapping } = getColorClassName(backgroundColor);
    const ctaList = filterValidCta(cta);

    return (
        <section
            id={customId ?? id} // id & data-section required for inner-page-navigation
            data-section
            className={`scroll-mt-35 ${sectionPaddingClassName} ${sectionColorMapping.bgColor}`}
        >
            <div className="container">
                <div className="row">
                    <div className="col-12">
                        {title && (
                            <h2
                                className={`text-center mb-5 xl:mx-auto ${sectionColorMapping.primaryTextColor} ${styles.heading}`}
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(title) }}
                            />
                        )}
                        {cardEyebrowText && (
                            <p
                                className={`${sectionColorMapping.secondaryTextColor} text-center text-md lg:text-2xl leading-relaxed tracking-tight6 font-light`}
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(cardEyebrowText) }}
                            />
                        )}
                        {body?.raw && <RichText data={cardItem} className={`${styles.body}`} />}
                        {cardMedia && (
                            <Media
                                media={cardMedia}
                                className="w-full h-auto lg:max-h-225 object-contain mt-8.5 lg:mt-10 xl:mt-20"
                            />
                        )}
                        {ctaList.map((linkData) => (
                            <Cta key={linkData.id} className="text-sm" data={linkData} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ContentWithImage;
